import { handleResponse } from './handleResponse';
import {
  BooklaAdminServiceSettings,
  BusinessService,
  CompanyService,
  CompanyServiceContent,
  CompanyServiceGroup,
  CompanyServiceSettings,
  CompanyServiceSettingsAdvanced,
  CompanyServiceSettingsCommon,
  CompanyServiceSettingsSlot,
  CompanyServiceSettingsTime,
  CompanyServiceTicket,
  CompanyServiceTicketTranslation,
  ServiceGroupFull,
  ServiceGroupPriority,
  ServiceGroupTranslation,
  ServiceSlotTemplate,
  ServiceTicket,
  ServiceTicketTitleTranslation,
  ServiceTicketGroupPriority,
  ServiceTranslation,
  CompanyServiceTicketGroupTranslation as CompanyServiceTicketGroupTranslationType,
  CompanyServiceTicketGroup,
  CompanyServiceTicketGroupListItem,
  CompanyServicePostEmailTemplate,
  PostEmailTemplateTranslation,
} from '../types/services';
import {
  CalendarServiceRelation,
  CalendarServiceRelationExisting,
  Category,
  CategoryWithTemplate,
  DeleteCalendarService,
  Image,
} from '../types/common';
import apiAxios from './apiAxios';
import { CalendarType, CompanyCalendarSettings } from '../types/calendars';
import { CompanyStateChange } from '@src/@data/types/company';
import {
  CustomForm,
  UpdateCustomFormResponse,
} from '@src/@data/types/customForms';

const servicesApi = {
  async listCompanyServiceGroups(
    companyId: string
  ): Promise<CompanyServiceGroup[]> {
    return apiAxios
      .get(`/api/portal/company/${companyId}/serviceGroups`)
      .then(handleResponse);
  },
  async listCompanyServicesWithGroups(
    companyId: string
  ): Promise<{ groups: CompanyServiceGroup[]; services: CompanyService[] }> {
    return apiAxios
      .get(`/api/portal/company/${companyId}/services`)
      .then(handleResponse);
  },
  async getService(
    companyId: string,
    serviceId: string
  ): Promise<CompanyService> {
    return apiAxios
      .get(`/api/portal/company/${companyId}/services/${serviceId}`)
      .then(handleResponse);
  },
  async getServiceContent(
    companyId: string,
    serviceId: string
  ): Promise<CompanyServiceContent> {
    return apiAxios
      .get(`/api/portal/company/${companyId}/services/${serviceId}/content`)
      .then(handleResponse);
  },
  async getServiceSettings(
    companyId: string,
    serviceId: string
  ): Promise<CompanyServiceSettings> {
    return apiAxios
      .get(`/api/portal/company/${companyId}/services/${serviceId}/settings`)
      .then(handleResponse);
  },
  async getCategories(): Promise<Category[]> {
    return apiAxios.get(`/api/portal/categories`).then(handleResponse);
  },
  async getCategoriesWithTemplates(): Promise<CategoryWithTemplate[]> {
    return apiAxios
      .get(`/api/portal/categoriesWithTemplates`)
      .then(handleResponse);
  },
  // Service groups
  async getServiceGroupFullInfo(
    companyId: string,
    serviceGroupId: string
  ): Promise<ServiceGroupFull> {
    return apiAxios
      .get(`/api/portal/company/${companyId}/serviceGroups/${serviceGroupId}`)
      .then(handleResponse);
  },
  async updateServiceGroupTranslations(
    companyId: string,
    serviceGroupId: string,
    translations: Map<string, ServiceGroupTranslation>
  ): Promise<void> {
    return apiAxios.put(
      `/api/portal/company/${companyId}/serviceGroups/${serviceGroupId}/edit/translations`,
      { translations: Object.fromEntries(translations) }
    );
  },
  // Service
  async updateServiceEnabled(
    companyId: string,
    serviceId: string,
    enabled: boolean
  ): Promise<CompanyStateChange> {
    return apiAxios
      .put(
        `/api/portal/company/${companyId}/services/${serviceId}/edit/enable`,
        {
          enabled,
        }
      )
      .then(handleResponse);
  },
  async updateServiceCustomForm(
    companyId: string,
    serviceId: string,
    translations: Map<string, CustomForm>
  ): Promise<UpdateCustomFormResponse> {
    return apiAxios
      .put(
        `/api/portal/company/${companyId}/services/${serviceId}/edit/customForm`,
        { translations: Object.fromEntries(translations) }
      )
      .then(handleResponse);
  },
  async updateContent(
    companyId: string,
    serviceId: string,
    content: {
      categoryId: string;
      genderPreference: string;
      location: string;
      serviceGroupId?: string;
    }
  ): Promise<void> {
    return apiAxios.put(
      `/api/portal/company/${companyId}/services/${serviceId}/edit/feed`,
      content
    );
  },
  async updateSettingsCommon(
    companyId: string,
    serviceId: string,
    settings: Omit<CompanyServiceSettingsCommon, 'currency'>
  ): Promise<void> {
    return apiAxios.put(
      `/api/portal/company/${companyId}/services/${serviceId}/edit/settings/common`,
      {
        ...settings,
      }
    );
  },
  async updateSettingsAdvanced(
    companyId: string,
    serviceId: string,
    settings: CompanyServiceSettingsAdvanced
  ): Promise<void> {
    return apiAxios.put(
      `/api/portal/company/${companyId}/services/${serviceId}/edit/settings/advanced`,
      {
        ...settings,
      }
    );
  },
  async updateTranslations(
    companyId: string,
    serviceId: string,
    translations: Map<string, ServiceTranslation>
  ): Promise<void> {
    return apiAxios.put(
      `/api/portal/company/${companyId}/services/${serviceId}/edit/translations`,
      {
        translations: Object.fromEntries(translations.entries()),
      }
    );
  },
  async updateTicketsTitleTranslations(
    companyId: string,
    serviceId: string,
    translations: Map<string, ServiceTicketTitleTranslation>
  ): Promise<void> {
    return apiAxios.put(
      `/api/portal/company/${companyId}/services/${serviceId}/edit/translations/ticketTitle`,
      {
        translations: Object.fromEntries(translations.entries()),
      }
    );
  },
  async updateImage(
    companyId: string,
    serviceId: string,
    imageID: string
  ): Promise<Image> {
    return apiAxios
      .put(
        `/api/portal/company/${companyId}/services/${serviceId}/edit/image`,
        {
          imageID,
        }
      )
      .then(handleResponse);
  },
  async deleteService(
    companyId: string,
    serviceId: string
  ): Promise<CompanyStateChange> {
    return apiAxios
      .delete(`/api/portal/company/${companyId}/services/${serviceId}`)
      .then(handleResponse);
  },
  async deleteServiceGroup(
    companyId: string,
    serviceGroupId: string
  ): Promise<void> {
    return apiAxios.delete(
      `/api/portal/company/${companyId}/serviceGroups/${serviceGroupId}`
    );
  },
  async createService(
    companyId: string,
    details: {
      title: string;
      type: CalendarType;
      spots: number;
      price: number;
      duration: number;
      categoryId: string;
      genderPreference: string;
      locationType: string;
      scheduleColor: number;
      calendars?: string[];
    }
  ): Promise<CompanyService> {
    return apiAxios
      .post(`/api/portal/company/${companyId}/services`, details)
      .then(handleResponse);
  },
  async getServiceCalendars(
    companyId: string,
    serviceId: string
  ): Promise<CalendarServiceRelationExisting[]> {
    return apiAxios
      .get(`/api/portal/company/${companyId}/services/${serviceId}/calendars`)
      .then(handleResponse);
  },
  async updateServiceCalendars(
    companyId: string,
    serviceId: string,
    setServiceCalendars: CalendarServiceRelation[],
    deleteServiceCalendars: DeleteCalendarService[]
  ): Promise<CompanyCalendarSettings> {
    return apiAxios.post(
      `/api/portal/company/${companyId}/services/${serviceId}/calendars`,
      {
        calendarServices: setServiceCalendars,
        disabled: deleteServiceCalendars,
      }
    );
  },
  async createServiceGroup(
    companyId: string,
    title: string
  ): Promise<ServiceGroupFull> {
    return apiAxios
      .post(`/api/portal/company/${companyId}/serviceGroups`, { title })
      .then(handleResponse);
  },
  async updateServiceGroupPriorities(
    companyId: string,
    priorities: ServiceGroupPriority[]
  ): Promise<void> {
    return apiAxios
      .put(`/api/portal/company/${companyId}/serviceGroups/edit/priorities`, {
        priorities,
      })
      .then(handleResponse);
  },
  async getBusinessServices(companyId: string): Promise<BusinessService[]> {
    return apiAxios
      .get(`/api/companies/${companyId}/services`)
      .then(handleResponse);
  },
  async getBusinessLinkedServices(
    companyId: string,
    calendarId: string
  ): Promise<BusinessService[]> {
    return apiAxios
      .get(`/api/companies/${companyId}/calendars/${calendarId}/services`)
      .then(handleResponse);
  },
  async getBusinessServiceTickets(
    companyId: string,
    serviceId: string
  ): Promise<ServiceTicket[]> {
    return apiAxios
      .get(`/api/companies/${companyId}/services/${serviceId}/tickets`)
      .then(handleResponse);
  },
  async cloneService(
    companyId: string,
    serviceId: string
  ): Promise<CompanyService> {
    return apiAxios
      .post(`/api/portal/company/${companyId}/services/${serviceId}/clone`)
      .then(handleResponse);
  },
  async createTicket(
    companyId: string,
    serviceId: string,
    data: {
      price: number;
      translations: Map<string, CompanyServiceTicketTranslation>;
    }
  ): Promise<CompanyServiceTicket> {
    return apiAxios
      .post(`/api/portal/company/${companyId}/services/${serviceId}/tickets`, {
        ...data,
        translations: Object.fromEntries(data.translations.entries()),
      })
      .then(handleResponse);
  },
  async editTicket(
    companyId: string,
    serviceId: string,
    ticketId: string,
    data: {
      updateSlots: boolean;
      price: number;
      groupId?: string;
      translations: Map<string, CompanyServiceTicketTranslation>;
    }
  ): Promise<CompanyServiceTicket> {
    return apiAxios
      .post(
        `/api/portal/company/${companyId}/services/${serviceId}/tickets/${ticketId}/edit/details`,
        {
          ...data,
          translations: Object.fromEntries(data.translations.entries()),
        }
      )
      .then(handleResponse);
  },
  async cloneTicket(
    companyId: string,
    serviceId: string,
    ticketId: string
  ): Promise<CompanyServiceTicket> {
    return apiAxios
      .post(
        `/api/portal/company/${companyId}/services/${serviceId}/tickets/${ticketId}/clone`
      )
      .then(handleResponse);
  },
  async changeTicketEnabled(
    companyId: string,
    serviceId: string,
    ticketId: string,
    isDisabled: boolean
  ): Promise<CompanyServiceTicket> {
    return apiAxios
      .post(
        `/api/portal/company/${companyId}/services/${serviceId}/tickets/${ticketId}/edit/enable`,
        {
          isDisabled,
        }
      )
      .then(handleResponse);
  },
  async deleteTicket(
    companyId: string,
    serviceId: string,
    ticketId: string
  ): Promise<void> {
    return apiAxios
      .delete(
        `/api/portal/company/${companyId}/services/${serviceId}/tickets/${ticketId}`
      )
      .then(handleResponse);
  },
  async updateSettingsTime(
    companyId: string,
    serviceId: string,
    settings: CompanyServiceSettingsTime
  ): Promise<void> {
    return apiAxios.put(
      `/api/portal/company/${companyId}/services/${serviceId}/edit/settings/time`,
      settings
    );
  },
  async updateSettingsSlot(
    companyId: string,
    serviceId: string,
    settings: CompanyServiceSettingsSlot
  ): Promise<void> {
    return apiAxios.put(
      `/api/portal/company/${companyId}/services/${serviceId}/edit/settings/slot`,
      settings
    );
  },
  async getServiceAdminSettings(
    companyId: string,
    serviceId: string
  ): Promise<BooklaAdminServiceSettings> {
    return apiAxios
      .get(
        `/api/portal/company/${companyId}/services/${serviceId}/adminSettings`
      )
      .then(handleResponse);
  },
  async updateServiceAdminSettings(
    companyId: string,
    serviceId: string,
    settings: Pick<BooklaAdminServiceSettings, 'explicitFeeEnabled'>
  ) {
    return apiAxios
      .put(
        `/api/portal/company/${companyId}/services/${serviceId}/edit/adminSettings`,
        {
          explicitFeeEnabled: settings.explicitFeeEnabled,
        }
      )
      .then(handleResponse);
  },
  async limitToClientGroups(
    companyId: string,
    serviceId: string,
    clientGroupIds: string[]
  ) {
    return apiAxios
      .put(
        `/api/portal/company/${companyId}/services/${serviceId}/edit/limitToClientGroups`,
        {
          clientGroupIds,
        }
      )
      .then(handleResponse);
  },
  async getServiceSlotTemplates(
    companyId: string,
    serviceId: string,
    filterInactive: boolean
  ): Promise<{ templates: ServiceSlotTemplate[] }> {
    return apiAxios
      .get(
        `/api/portal/company/${companyId}/services/${serviceId}/slotTemplates`,
        { params: { filterInactive } }
      )
      .then(handleResponse);
  },
  async deleteServiceSlotTemplate(
    companyId: string,
    serviceId: string,
    templateId: string
  ): Promise<void> {
    return apiAxios
      .delete(
        `/api/portal/company/${companyId}/services/${serviceId}/slotTemplates/${templateId}`
      )
      .then(handleResponse);
  },
  async updateServiceTicketGroupPriorities(
    companyId: string,
    serviceId: string,
    priorities: ServiceTicketGroupPriority[]
  ): Promise<void> {
    return apiAxios
      .put(
        `/api/portal/company/${companyId}/services/${serviceId}/ticketGroups/edit/priorities`,
        {
          priorities,
        }
      )
      .then(handleResponse);
  },
  async createServiceTicketGroup(
    companyId: string,
    serviceId: string,
    data: {
      translations: Map<string, CompanyServiceTicketGroupTranslationType>;
    }
  ): Promise<CompanyServiceTicketGroup> {
    return apiAxios
      .post(
        `/api/portal/company/${companyId}/services/${serviceId}/ticketGroups`,
        {
          translations: Object.fromEntries(data.translations.entries()),
        }
      )
      .then(handleResponse);
  },
  async deleteServiceTicketGroup(
    companyId: string,
    serviceId: string,
    ticketGroupId: string
  ): Promise<void> {
    return apiAxios
      .delete(
        `/api/portal/company/${companyId}/services/${serviceId}/ticketGroups/${ticketGroupId}`
      )
      .then(handleResponse);
  },

  async updateServiceTicketGroupTranslations(
    companyId: string,
    serviceId: string,
    ticketGroupId: string,
    data: {
      translations: Map<string, CompanyServiceTicketGroupTranslationType>;
    }
  ): Promise<CompanyServiceTicketGroup> {
    return apiAxios
      .put(
        `/api/portal/company/${companyId}/services/${serviceId}/ticketGroups/${ticketGroupId}/edit/translations`,
        {
          translations: Object.fromEntries(data.translations.entries()),
        }
      )
      .then(handleResponse);
  },

  async listCompanyServiceTicketGroups(
    companyId: string,
    serviceId: string
  ): Promise<CompanyServiceTicketGroupListItem[]> {
    return apiAxios
      .get(
        `/api/portal/company/${companyId}/services/${serviceId}/ticketGroups`
      )
      .then(handleResponse);
  },

  async listPostReservationEmailTemplates(
    companyId: string,
    serviceId: string
  ): Promise<CompanyServicePostEmailTemplate[]> {
    return apiAxios
      .get(
        `/api/portal/company/${companyId}/services/${serviceId}/customEmailTemplates`
      )
      .then(handleResponse);
  },

  async createPostReservationEmailTemplate(
    companyId: string,
    serviceId: string,
    data: {
      translations: Map<string, PostEmailTemplateTranslation>;
      isHtml: boolean;
    }
  ): Promise<CompanyServicePostEmailTemplate> {
    return apiAxios
      .post(
        `/api/portal/company/${companyId}/services/${serviceId}/customEmailTemplates`,
        {
          translations: Object.fromEntries(data.translations.entries()),
          isHtml: data.isHtml,
        }
      )
      .then(handleResponse);
  },

  async updatePostReservationEmailTemplate(
    companyId: string,
    serviceId: string,
    templateId: string,
    data: {
      translations: Map<string, PostEmailTemplateTranslation>;
      isHtml: boolean;
    }
  ): Promise<CompanyServicePostEmailTemplate> {
    return apiAxios
      .put(
        `/api/portal/company/${companyId}/services/${serviceId}/customEmailTemplates/${templateId}`,
        {
          translations: Object.fromEntries(data.translations.entries()),
          isHtml: data.isHtml,
        }
      )
      .then(handleResponse);
  },
};

export default servicesApi;
